import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Container, Typography, Link, List, ListItem, Paper, Grid, ListItemText, IconButton, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { colors } from '../styles';
import LinkIcon from '@mui/icons-material/Link';

// Privacy Policy 2.6 is the v1.3 advertising and measurement disclosure
// (#2440). 2.5 (#2300) is the free-trial record disclosure. The X Pixel
// (#2439) and Meta Pixel (#2442) lines in How We Share are same-day
// amendments to 2.6, not a new version: bumping CURRENT_VERSIONS.privacy
// re-prompts every user. 2.7 (Jared decided in chat 2026-10-18) has the same
// text as amended 2.6 and exists to re-prompt everyone once for the
// server-side sharing with TikTok, X and Google Analytics (#2504).
// Last updated is the merge date. Legal set 10/18/2026 because merge is the
// 18th or later.
const PRIVACY_POLICY_VERSION = '2.7';
const PRIVACY_POLICY_LAST_UPDATED = '10/18/2026';

const FREE_TRIAL_RECORD_PARAGRAPH = 'Free-trial record. When your account is deleted, we keep a limited record so that each person can use a free trial only once. It contains a one-way, keyed cryptographic hash of your email address and of your phone number (if you gave us one), the type of trial you used, its start and end dates, the date we created the record, and a note of how it was created (for example, because your account was deleted). The hash is designed so that we cannot read your email address or phone number from it; we can only use it to check whether the same email address or phone number signs up again. The record contains nothing else about you, and we use it only to check free-trial eligibility. We delete it automatically three years after the record is created, which is normally when your account is deleted.';

const ACCOUNT_DELETION_RETENTION_SENTENCE = 'When your account is deleted, we keep only the limited free-trial record described under Data Retention.';

const StyledSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '20px',
  backgroundColor: colors.white,
  marginBottom: theme.spacing(3),
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
  position: 'relative'
}));

const FloatingMenu = styled(Paper)(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing(4),
  backgroundColor: colors.white,
  width: '100%',
  marginRight: theme.spacing(4),
  alignSelf: 'flex-start',
  boxShadow: 'none',
  borderRadius: '20px',
  padding: theme.spacing(2)
}));

const MenuLink = styled(ListItem)(({ theme, active }) => ({
  borderRadius: '0.5rem',
  marginBottom: theme.spacing(0.5),
  cursor: 'pointer',
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  transition: 'transform 80ms cubic-bezier(0, 0.3, 1, 0.5), background 80ms cubic-bezier(0, 0.3, 1, 0.5)',
  '&:hover': {
    backgroundColor: active ? `${theme.palette.primary.main}20` : 'rgba(0, 0, 0, 0.04)',
    borderRadius: '0.5rem'
  },
  '&:active': {
    transform: 'scale(0.95)'
  },
  ...(active && {
    backgroundColor: `${theme.palette.primary.main}20`
  }),
  '& .MuiTypography-root': {
    fontWeight: active ? 900 : 600,
    fontSize: '1.1rem'
  }
}));

const Section = ({ id, title, children, onVisible }) => {
  const sectionRef = useRef(null);
  const [shouldObserve, setShouldObserve] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShouldObserve(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!shouldObserve) return;
    const node = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.intersectionRatio >= 0.5) {
          onVisible && onVisible(id);
        }
      },
      { threshold: 0.5, rootMargin: '-80px 0px' }
    );
    if (node) observer.observe(node);
    return () => { if (node) observer.unobserve(node); };
  }, [id, onVisible, shouldObserve]);

  return (
    <StyledSection ref={sectionRef}>
      <div id={id} style={{ position: 'absolute', top: 0 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: colors.primary }}>
          {title}
        </Typography>
        <Tooltip title="Copy link" placement="left">
          <IconButton
            size="small"
            aria-label={`Copy link to ${title}`}
            onClick={() => {
              try {
                const origin = window.location.origin;
                const path = window.location.pathname;
                const url = `${origin}${path}#${id}`;
                navigator.clipboard.writeText(url);
              } catch (error) {
                console.error('Failed to copy section link', error);
              }
            }}
          >
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ color: colors.secondary }}>{children}</Box>
    </StyledSection>
  );
};

const Privacy = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const [isManualScroll, setIsManualScroll] = useState(false);
  const scrollTimeout = useRef(null);

  const sections = useMemo(() => [
    { id: 'overview', label: 'Overview' },
    { id: 'information-we-collect', label: 'Information We Collect' },
    { id: 'plaid-notice', label: 'Plaid Notice' },
    { id: 'how-we-use-information', label: 'How We Use Information' },
    { id: 'ai-processing', label: 'AI Processing' },
    { id: 'eea-uk-legal-bases', label: 'EEA/UK Legal Bases' },
    { id: 'how-we-share-information', label: 'How We Share Information' },
    { id: 'analytics-and-session', label: 'Cookies and Similar Technologies' },
    { id: 'text-messages', label: 'Text Messages (SMS)' },
    { id: 'social-and-community-features', label: 'Social & Community Features' },
    { id: 'your-rights', label: 'Your Rights' },
    { id: 'security-and-data-anonymization', label: 'Security & Anonymization' },
    { id: 'data-retention', label: 'Data Retention' },
    { id: 'childrens-privacy', label: "Children's Privacy" },
    { id: 'international-data-transfers', label: 'International Transfers' },
    { id: 'changes-to-this-policy', label: 'Changes' },
    { id: 'contact-us', label: 'Contact' }
  ], []);

  const handleSectionVisible = useCallback((id) => {
    if (isManualScroll) return;
    setActiveSection(id);
    try {
      const path = window.location.pathname;
      window.history.replaceState(null, '', `${path}#${id}`);
    } catch (error) {
      console.warn('Unable to update URL hash', error);
    }
  }, [isManualScroll]);

  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (!element) return;
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);
    setIsManualScroll(true);
    setActiveSection(sectionId);
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    window.scrollBy({ top: -80, behavior: 'smooth' });
    scrollTimeout.current = setTimeout(() => setIsManualScroll(false), 1000);
    try {
      const path = window.location.pathname;
      window.history.replaceState(null, '', `${path}#${sectionId}`);
    } catch (error) {
      console.warn('Unable to update URL hash', error);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && sections.some(s => s.id === hash)) {
      const timer = setTimeout(() => scrollToSection(hash), 300);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const SideMenu = () => (
    <FloatingMenu>
      <List>
        {sections.map((s) => (
          <MenuLink key={s.id} active={activeSection === s.id} onClick={() => scrollToSection(s.id)} disableRipple>
            <ListItemText 
              primary={s.label}
              primaryTypographyProps={{
                fontWeight: activeSection === s.id ? 900 : 600,
                fontSize: '0.95rem',
                color: 'inherit'
              }}
            />
          </MenuLink>
        ))}
      </List>
    </FloatingMenu>
  );

  return (
    <Box sx={{
      backgroundColor: colors.background,
      minHeight: '100vh',
      py: { xs: 6, md: 10 },
      overflowY: 'auto',
      height: '100vh',
      position: 'fixed',
      width: '100%'
    }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" sx={{ fontWeight: 900, color: colors.primary, mb: 1 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body2" sx={{ color: colors.secondary, opacity: 0.8 }}>
            Last updated: {PRIVACY_POLICY_LAST_UPDATED} &nbsp;|&nbsp; Policy Version: {PRIVACY_POLICY_VERSION}
          </Typography>
        </Box>

        <Grid container spacing={0}>
          <Grid item xs={12} md={3} sx={{ display: { xs: 'block', md: 'none' }, mb: 3 }}>
            <SideMenu />
          </Grid>

          <Grid item xs={12} md={9} sx={{ pr: { xs: 0, md: 4 } }}>

        <Section id="overview" title="Overview" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Bleau Consulting LLC, a Kansas limited liability company doing business as Budgit ("Budgit," "we," "our," or "us"), respects your privacy. This Privacy Policy explains what information we collect, how we use it, and your rights. This policy applies to our website, web app, and mobile apps (the "Services"). By using the Services, you agree to this policy.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Controller identity and contact:
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2, whiteSpace: 'pre-line' }}>
            {`Bleau Consulting LLC, doing business as Budgit
11985 S. Monticello Terrace
Olathe, KS 66061
support@budgit.app`}
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            Budgit is the data controller responsible for your information. If you are located in the European Economic Area (EEA), the United Kingdom, or other regions with data protection laws, you may have additional rights described below.
          </Typography>
        </Section>

        <Section id="information-we-collect" title="Information We Collect" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            We collect the following categories of information:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Identifiers: email address, phone number, IP address, device identifiers</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Account information: preferences, profile details</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Commercial information: subscription status, transactions processed through Plaid</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Financial account and transaction data obtained via Plaid (merchant names, amounts, categories, account metadata as provided by your institution)</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Images and documents you upload (e.g., receipts and bills) for extraction and categorization</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">AI feedback (e.g., category corrections) that you provide in the product</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Approximate usage analytics and, where enabled, session diagnostics (see "Analytics and Session Technologies")</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Internet activity: app usage, log data, browser type, cookies</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Geolocation data: approximate location when enabled by your device</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Support communications: when you contact us for help or provide feedback</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">User-generated content: profile images, names, achievement events, streaks, scores, comments, reactions, and other content you submit to leaderboards, the Discover community feed, or contests</Typography>
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            When you connect financial accounts via Plaid, we access transaction and account data on your behalf.
          </Typography>
        </Section>

        <Section id="plaid-notice" title="Plaid Notice" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            We use Plaid Inc. ("Plaid") to connect your financial accounts. When you choose to connect a financial account, you
            authorize Budgit to securely transmit your information to Plaid and authorize Plaid to obtain your data from your
            financial institution. We do not see or store your bank login credentials.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            For more information, see
            {' '}<Link href="https://plaid.com/legal/#end-user-privacy-policy" target="_blank" rel="noopener noreferrer">Plaid's End User Privacy Policy</Link>.
          </Typography>
        </Section>

        <Section id="how-we-use-information" title="How We Use Information" onVisible={handleSectionVisible}>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Provide, maintain, and improve the Services</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Classify transactions and generate budgeting insights</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Process transactions and documents with AI systems to categorize spending and generate budgeting insights</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Improve AI models for your account and, where disclosed and permitted, for the Service generally (see "AI Processing and Model Improvement")</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Operate optional social and contest features you enable</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Detect fraud, abuse, and security incidents</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Communicate with you about updates and support</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Protect against fraud, abuse, and misuse</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Comply with legal obligations</Typography>
            </ListItem>
          </List>
        </Section>

        <Section id="ai-processing" title="AI Processing and Model Improvement" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            We use third-party AI processors, including OpenAI, to classify transactions, extract information from receipt and bill images, and generate budgeting insights. Transaction fields and uploaded images may be sent to those processors for that purpose.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Per-user improvement:</strong> your corrections and usage may be used to improve models and features for your account.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Pooled improvement:</strong> pooled training that uses data from more than one user is not currently performed and is paused. If we resume pooled training or fine-tuning across users, we will (i) disclose that practice here, (ii) rely on an appropriate legal basis (for EEA/UK users, typically legitimate interests balanced against your rights, or consent where required), and (iii) provide an in-product control to opt out of pooled training where that feature is offered. We will not claim that we train shared models on your data while pooled training is paused.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            We do not use your Financial Data to train public foundation models for unrestricted third-party use. Processors act as service providers under written terms.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            Marketing statements that data is "anonymous" must not overclaim: transaction histories can be re-identifiable. We use encryption, access controls, and de-identification or aggregation where feasible; we do not guarantee that all derived data is anonymous under every legal definition.
          </Typography>
        </Section>

        <Section id="eea-uk-legal-bases" title="EEA/UK Legal Bases" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            If you are located in the EEA or UK, our legal bases for processing include:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Performance of a contract (providing the Services you request)</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Consent (for marketing communications, optional features)</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Legitimate interests (improving Services, protecting against abuse)</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Legal obligations (regulatory or tax compliance)</Typography>
            </ListItem>
          </List>
        </Section>

        <Section id="how-we-share-information" title="How We Share Information" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            We do not sell your personal information for money. We share information with service providers that process data on our behalf, including:
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Cloud hosting providers (e.g., AWS, Azure, GCP)</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Payment processors (e.g., Stripe; Apple for iOS In-App Purchase)</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Financial connectivity providers (e.g., Plaid)</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">AI processors (e.g., OpenAI) for classification, extraction, and insights</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Analytics tools (Google Analytics), only after you accept that choice on budgit.app</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Advertising measurement pixels (the Reddit Pixel), loaded on budgit.app only after you accept that choice; they receive the event type and, for a subscription, the amount and currency, never your account id, email, or payment identifiers</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Advertising measurement tags (the LinkedIn Insight Tag), loaded on budgit.app only after you accept that choice; it receives the page you visited and a conversion event id for a download click, signup, or subscription, never your account id, email, or payment identifiers</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Advertising measurement pixels (the X Pixel), loaded on budgit.app only after you accept that choice; it receives the page you visited, a conversion event for a download click, signup, or subscription, a random event id with signup and subscription events, and, for a subscription, the amount and currency, never your account id, email, or payment identifiers</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Advertising measurement pixels (the Meta Pixel), loaded on budgit.app only after you accept that choice; it receives the page you visited and, for a signup or subscription, a conversion event with an event id that lets Meta match it to the server record described below (for a signup, a hashed account identifier; for a subscription, the invoice or checkout reference from our payment processor), and, for a subscription, the amount and currency, never your email, phone number, unhashed account id, or card details</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Advertising measurement partners (Meta, TikTok and X) and Google Analytics, from our servers. When you sign up, start a trial, or buy your first subscription, on budgit.app or in the iOS or Android app, our servers may send a record of that event to Meta, TikTok and X so they can measure our ads, and to Google Analytics. Meta and TikTok receive a hashed (one-way encrypted) email address, phone number (if you gave us one), and account identifier. X receives a hashed email address. Each record also includes the event type and time, whether it came from the web, iOS, or Android, an event id (for a purchase, the invoice or transaction reference from our payment processor or Apple), the amount and currency for a purchase, and, where we have them, your IP address and browser or app user agent. Subscription renewals are not sent to Meta, TikTok, or X. Google Analytics receives your Budgit account id (not your email or phone number), the event type and time, whether you used the web, iOS, or Android, and, for a purchase or renewal, the plan, amount, currency, and transaction reference. This sharing is not tied to your cookie choice.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Ad click identifiers, only after you accept that choice on budgit.app. If you reach budgit.app from an ad, we keep the click identifier from that visit and Meta's browser identifier. Budgit's copy in your browser expires after 7 days. While you accept that choice, we also read Meta's browser cookies. On your account, we keep them until you reject that choice or delete your account. We only include identifiers from the last 30 days in the server records above, and only those from Meta, TikTok, and X. We store Google's click identifier but do not send it. If you reject that choice, we delete Budgit's stored copy from your browser and from your account (if you are signed out, the next time you sign in on that browser), and we stop reading Meta's browser cookies. Those cookies belong to Meta, and we do not delete them.</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">App analytics in the Android app and some iOS app builds (Google's Firebase Analytics), which may record in-app events such as signup, trial start, and subscription purchase</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Session diagnostics / experience tools (e.g., Microsoft Clarity), only as configured and, where required, with appropriate consent or disclosure</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Email and customer support providers</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Messaging providers for texts (e.g., Twilio) and wireless carriers</Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">Gift card and reward providers (e.g., Tremendous), only to deliver rewards you earn</Typography>
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            We may also disclose information if required by law, legal process, or to protect Budgit's rights, users, or security.
          </Typography>
        </Section>

        <Section id="analytics-and-session" title="Cookies and Similar Technologies" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            We and our providers use cookies, SDKs, and similar technologies for authentication, preferences, analytics and advertising measurement (only after you accept on budgit.app), and (where enabled) session diagnostics and experience tooling. Those analytics and advertising measurement tools may set their own cookies.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Marketing website.</strong> On budgit.app, we offer an accept or reject choice for optional analytics and advertising measurement tools. Those tools load only after you accept, or if you previously granted that choice. If you reject, or you have not chosen, they do not load. The tools that choice covers are listed under How We Share Information. This is not a full consent-management platform. If we expand marketing reach in the EEA/UK, we will add mechanisms appropriate to that reach. Session diagnostics on the marketing site, if used, are disclosed here and configured with the same honesty about what is and is not gated.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Authenticated web app.</strong> Where Microsoft Clarity or similar session tools are enabled, we configure them with controls such as consent or configuration gates, element masking, and limits on recording sensitive financial UI. We will not enable recording that is intended to capture sensitive financial screens without a lawful basis and disclosure consistent with this policy.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Mobile apps (including iOS).</strong> Where configured, we may use Microsoft Clarity (or similar) for session diagnostics and product experience. On iOS, the Clarity native module may initialize when the app launches so capture is reliable; additional setup after sign-in may attach an internal user identifier (not your email) and apply product controls. Controls include pausing or masking capture on sensitive screens such as authentication, PIN entry, and payment or upgrade flows. Mobile OS settings may also limit some advertising or tracking identifiers. Android may forward legal-consent status to the Clarity consent API where the SDK supports it; iOS support for that API may be limited by the vendor SDK.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>Advertising and measurement.</strong> On iOS, Budgit uses Apple Search Ads attribution and Apple install-measurement postbacks (SKAdNetwork and AdAttributionKit). The iOS app itself does not include advertising SDKs, does not ask for tracking permission, does not collect the advertising identifier (IDFA), and does not send data to Meta or TikTok from your device; signup, trial and subscription events may be sent from our servers as described under How We Share Information. The Android app does not include Meta or TikTok SDKs and does not send data to Meta or TikTok from your device. It includes Google's Firebase Analytics SDK, listed under How We Share Information. Conversion events for Meta, TikTok, and X come only from our servers. Android does not collect the iOS Identifier for Advertisers and does not show a tracking prompt.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            <strong>What we do not claim.</strong> Account deletion in Budgit does not guarantee deletion of Clarity session recordings, analytics logs held by providers, backups, or third-party training corpora. Provider retention follows their terms and our configuration.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            You can control many cookies through browser settings. To change your analytics and advertising measurement choice on budgit.app, clear this site's data in your browser and choose again when the banner appears, or use the Cookie choices link at the bottom of any page. Questions: <Link href="mailto:support@budgit.app">support@budgit.app</Link>.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            <strong>Email.</strong> Our marketing emails may contain a small tracking pixel and links that pass through our servers, so we can see whether an email was opened, which links were clicked, and whether it led to a booking or subscription. Some email apps open messages automatically, so open data is approximate. You can block the pixel by turning off images in your email app, and you can stop marketing emails anytime with the unsubscribe link. We keep this data with your account and delete it when your account is deleted.
          </Typography>
        </Section>

        <Section id="text-messages" title="Text Messages (SMS)" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            If you give us your phone number, we use it to send sign-in codes. We send marketing or promotional texts only if you separately opt in. Those texts may include offers, product updates, and feedback invitations. Message frequency varies. Msg &amp; data rates may apply. You can opt out anytime by replying STOP, or by turning off texts in Settings. Reply HELP or email <Link href="mailto:support@budgit.app">support@budgit.app</Link> for help. We record your consent (time, IP address, and the wording you agreed to) to show you opted in. We share your phone number only with our messaging provider (Twilio) and the wireless carriers needed to deliver texts. We do not sell, rent, or share your phone number or text-messaging opt-in data with third parties or affiliates for their own marketing. Opting out of marketing texts doesn't stop sign-in codes you request.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            We keep a record of your text-message consent and opt-outs for up to 5 years, even after you opt out or delete your account, so we can show we followed the law.
          </Typography>
        </Section>

        <Section id="social-and-community-features" title="Social & Community Features" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Budgit includes optional social features such as leaderboards, the Discover community feed, achievement events, streaks, and the Monthly Budgeting Challenge. When you use these features, certain information about you may be displayed to other Budgit users within the app.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2, fontWeight: 600 }}>
            What may be displayed to other users
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">
                <strong>Profile image and name</strong> — if you set a profile image, it may appear next to your entry on leaderboards, in the Discover community feed, in contest displays, and in other social UI. Your first name and last initial may be shown alongside it.
              </Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">
                <strong>Achievement events</strong> — events such as reaching a streak milestone, approving a budget, winning a challenge, or earning a badge may be posted to the Discover community feed where other users can see, react to, or comment on them.
              </Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">
                <strong>Leaderboard standings</strong> — your budgeting score, current streak, and rank may be visible to other participants in the Monthly Budgeting Challenge and related leaderboards.
              </Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">
                <strong>Comments, reactions, and posts</strong> — if you post a comment, reaction, or other content to the community feed, it will be attributed to you and visible to other users who can access that feed.
              </Typography>
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2, mb: 2 }}>
            We do not share transaction amounts, account balances, bank names, or other financial account details as part of these social features. Only the aggregated and app-generated information described above is displayed.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2, fontWeight: 600 }}>
            Your opt-out controls
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            A new Budgit account has Community sharing turned on. You can turn it off at any time from Profile settings. An account that already exists is not turned on by this change. A saved Community choice stays as it is. An account with no saved Community choice keeps Community sharing off. Joining the leaderboard is a separate choice and stays off until you turn it on.
          </Typography>
          <List sx={{ listStyleType: 'disc', pl: 3 }}>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">
                <strong>Leaderboard Opt-Out</strong> — this setting is on by default (you are not visible). Turn it off to appear on leaderboards. Turn it back on at any time to remove yourself.
              </Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', pl: 0 }}>
              <Typography variant="body1">
                <strong>Feed Opt-Out</strong> — A new account starts with Community sharing on, so this setting starts off. An account that already exists is not switched on. A saved choice stays as it is. An account with no saved Community choice keeps Community sharing off. Turn this setting on to stop sharing your events and to hide the Community feed. Turn it off to share again.
              </Typography>
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            You can also remove or replace your profile image at any time from your Profile settings. Content you previously posted to the community feed may remain visible until you delete it or your account. See our <Link href="/terms#user-generated-content">Terms of Service</Link> for information about the license you grant to Budgit for user-generated content.
          </Typography>
        </Section>

        <Section id="your-rights" title="Your Rights" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Depending on your location, you may have rights to: access; correct; delete; port; object to or restrict certain processing; opt out of marketing; and opt out of "sale" or "sharing" of personal information as those terms are defined under applicable U.S. state laws (Budgit does not sell personal information for money).
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            To exercise these rights, email <Link href="mailto:support@budgit.app">support@budgit.app</Link>. We will verify your request and respond as required by law. Some rights are limited (e.g., we may retain data needed for legal compliance, fraud prevention, or completed transactions).
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            <strong>Account deletion:</strong> when self-serve deletion is available in the product, that path will also be described here. Until then, email support@budgit.app to request deletion. Deletion requests should include associated AI artifacts where Budgit controls them (e.g., per-user fine-tuned artifacts), subject to technical feasibility and legal retention. {ACCOUNT_DELETION_RETENTION_SENTENCE}
          </Typography>
        </Section>

        <Section id="security-and-data-anonymization" title="Security and Data Anonymization" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            We use industry-standard measures to protect your information, including encryption of sensitive data. Where feasible,
            we anonymize or pseudonymize personal data (e.g., storing searchable hashes instead of plaintext).
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            In the event of a data breach affecting your information, we will notify you and regulators as required by law. No
            method of transmission or storage is 100% secure.
          </Typography>
        </Section>

        <Section id="data-retention" title="Data Retention" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            We retain information for as long as your account is active or as needed to provide Services. We may also retain
            information to comply with legal obligations, resolve disputes, and enforce agreements. When no longer needed, data is
            deleted or anonymized.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mt: 2 }}>
            {FREE_TRIAL_RECORD_PARAGRAPH}
          </Typography>
        </Section>

        <Section id="childrens-privacy" title="Children's Privacy" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            The Services are not directed to anyone under 18, and we do not knowingly collect personal information from anyone under 18. If we learn that we have collected personal information from a person under 18, we will delete it. If you believe a minor has provided us information, contact support@budgit.app.
          </Typography>
        </Section>

        <Section id="international-data-transfers" title="International Data Transfers" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            If you access the Services from outside the United States, your information may be transferred to and processed in the
            U.S. and other countries. These countries may have different data protection laws, but we take steps to ensure your data is
            protected.
          </Typography>
        </Section>

        <Section id="changes-to-this-policy" title="Changes to This Policy" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            We may update this policy from time to time. We will post the updated policy here and update the "Last updated"
            date above. If changes are material, we may provide additional notice.
          </Typography>
        </Section>

        <Section id="contact-us" title="Contact Us" onVisible={handleSectionVisible}>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Questions or concerns? Contact Bleau Consulting LLC, doing business as Budgit, at <Link href="mailto:support@budgit.app">support@budgit.app</Link>, or write to:
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, whiteSpace: 'pre-line' }}>
            {`Bleau Consulting LLC, doing business as Budgit
11985 S. Monticello Terrace
Olathe, KS 66061`}
          </Typography>
        </Section>
          </Grid>
          <Grid item xs={12} md={3} sx={{ display: { xs: 'none', md: 'block' } }}>
            <SideMenu />
          </Grid>
        </Grid>

      </Container>
    </Box>
  );
};

export default Privacy;


